import { staleRecipeWriteError } from '../recipeWriteConflict';
import { getAccessToken } from '../authentication/supabaseService';
import {
  practiceCostPriceById,
  practiceCreateCostPrice,
  practiceDeleteCostPrice,
  practiceUpdateCostPrice,
  withPracticeCostPrices,
} from '../../components/onboarding/practice/practiceGuards';

// Mirror of the server `IngredientAlternative` (server/domains/costPrice/costPriceTypes.ts).
// An alternative product for an ingredient — another real order-history product the
// kitchen sometimes buys instead of the primary, with its OWN per-portion quantity/
// unit and optional waste%. Cost math ignores alternatives; only the waste-allocation
// engine uses them. No prices persisted (golden rule).
export interface IngredientAlternative {
  id: string; // regenerated on save, like ingredient ids
  articleId: string;
  productName: string;
  platform: string;
  customPlatformName?: string;
  quantityNeeded: number; // per portion, in THIS product's std unit
  quantityInfo: {
    stdAmount: number;
    stdMultiplier: number;
    stdUnit: string;
  };
  // undefined => restaurant default waste% (NOT the primary's).
  wastePercentage?: number;
}

export interface CostPriceIngredient {
  id: string;
  // Stable template-line identity, retained when its chosen ingredient is swapped.
  automationTemplateKey?: string;
  // Normal-ingredient fields (undefined when this row is a subrecipe reference
  // or a nested-cost-price reference).
  articleId?: string;
  productName?: string;
  quantityNeeded?: number;
  quantityInfo?: {
    stdAmount: number;
    stdMultiplier: number;
    stdUnit: string;
  };
  currentPrice?: number;
  currentPriceDate?: Date;
  platform?: string;
  customPlatformName?: string;
  custom?: boolean;
  // Per-ingredient waste %. undefined = inherit the restaurant-wide default.
  wastePercentage?: number;
  // Alternative products — allowed ONLY on order-history ingredient rows (stripped
  // on write for custom / subrecipe-ref / nested-cost-price rows). Cost math ignores
  // them; used only by the waste-allocation engine.
  alternatives?: IngredientAlternative[];
  // Subrecipe-reference fields (post-migration this row is JUST these + id).
  subrecipeId?: string;
  subrecipePortionsNeeded?: number;
  // Legacy snapshot fields — preserved on the type only so older code that
  // reads them still compiles. After the M35 backfill, new reference rows do
  // NOT carry these; UI should look them up live from the subrecipes store.
  subrecipeName?: string;
  subrecipeTotalPortions?: number;
  subrecipePortionsUnit?: string;
  // Nested cost price reference fields
  nestedCostPriceId?: string;
  nestedCostPriceName?: string;
  nestedQuantityNeeded?: number;
  // Cross-restaurant sourcing: when set, this ingredient is sourced from a sibling
  // restaurant and its price is computed live from that restaurant's data.
  // Absent = current restaurant (today's behaviour). Price is NOT snapshotted.
  sourceRestaurantId?: string;
  sourceRestaurantName?: string; // snapshot of the name for display only
}

// BYO = build-your-own. Per-dish add-on quantity overrides, stored on the
// PARENT dish: { [choiceName]: { [ingredientKey]: quantity } }. The ingredient
// key is a stable content identity (row ids are regenerated on every update):
// "sub:<subrecipeId>" | "nested:<nestedCostPriceId>" | "<platform>:<articleId>".
// The quantity replaces the matching row's quantityNeeded /
// subrecipePortionsNeeded / nestedQuantityNeeded when costing this dish's
// picks of that choice. Unknown keys are ignored. Mirrors the server type.
export type ByoChoiceOverrides = Record<string, Record<string, number>>;

export interface CostPriceAnalysis {
  id: string;
  restaurantId: string;
  costPriceName: string; // display name for this cost price item
  menuItemName?: string; // optional link to revenue history item name
  categoryId?: string;
  ingredients: CostPriceIngredient[];
  totalCost: number;
  customMenuPrice?: number;
  customMenuPriceExVat?: number;
  isCustomItem: boolean; // true if this is a custom item not from revenue data
  customItemPrice?: number; // the price for custom items
  customItemPriceExVat?: number;
  costPriceType?: string;
  byoChoiceOverrides?: ByoChoiceOverrides;
  automationRunId?: string | null;
  automationReviewedAt?: string | null;
  automationReviewFlagged?: boolean;
  automationReviewNote?: string | null;
  markedIrrelevant?: boolean; // excluded from setup-score denominators when true
  createdAt: Date;
  updatedAt: Date;
  updatedAtToken?: string;
  restaurantName?: string; // Added when fetching all restaurants
}

// BYO = build-your-own (e.g. a wok dish where the customer picks a protein, a
// carb, and sauces). When `enrichCostPriceWithCurrentPrices` detects a dish has
// a non-empty `choices` array in revenue within the weighting window, it attaches
// this breakdown to the returned enriched analysis. Enrich-only — never
// persisted, never sent to / from the API.
export interface ByoCostBreakdown {
  baseCost: number;            // dish's own-ingredient cost (no choices added)
  baseQty: number;             // units of the dish sold in the window
  // One entry per choice name in the dish's linked revenue items (window union).
  // `isCosted=false` means there is no CHOICE analysis for this name yet —
  // `perPickCost` is treated as 0 in the weighted formulas (decision 9).
  choices: Array<{
    name: string;
    perPickCost?: number;
    qtySold: number;
    unitPrice: number;         // avg paid surcharge per pick (incl-VAT); the CHOICE's customMenuPrice when set
    unitPriceExVat: number;    // ditto, ex-VAT
    isCosted: boolean;
  }>;
  weightedDishCost: number;            // baseCost·baseQty + Σ perPickCost·qtySold
  weightedAvgCostPerDish: number;      // weightedDishCost / baseQty → totalCost
  weightedMenuPrice: number;           // baseItemPrice + Σ choice revenue / baseQty, incl-VAT
  weightedMenuPriceExVat: number;      // same, ex-VAT
  baseItemPrice: number;               // base portion only (incl-VAT): the dish's customMenuPrice when set, else Σ base revenue / baseQty
  baseItemPriceExVat: number;          // ditto, ex-VAT — needed by the composer
}

/**
 * Returned by `enrichCostPriceWithCurrentPrices` / `enrichCostPriceListWithCurrentPrices`.
 * Adds the enrich-only `byo` field — the base `CostPriceAnalysis` API type stays
 * clean. `byo` is undefined for non-BYO dishes (i.e. the dish's menu item has
 * no choices in the weighting window, or no `menuItemName` is set).
 */
export type EnrichedCostPriceAnalysis = CostPriceAnalysis & { byo?: ByoCostBreakdown };

/**
 * Serialize an ingredient into the shape accepted by create/update requests,
 * stripping only the enrich-only / identity fields (`id`, `currentPrice`,
 * `currentPriceDate`). Spread-based on purpose: any new field on
 * `CostPriceIngredient` (e.g. `sourceRestaurantId`) is preserved automatically,
 * so subsequent edits never silently drop it. Replaces the hand-written field
 * whitelists that used to live in every update call site.
 */
export function toIngredientUpdatePayload(
  ingredient: CostPriceIngredient
): Omit<CostPriceIngredient, 'id' | 'currentPrice' | 'currentPriceDate'> {
  const { id, currentPrice, currentPriceDate, ...rest } = ingredient;
  return rest;
}

export interface CostPriceCreateRequest {
  automationRunId?: string;
  restaurantId: string;
  costPriceName: string;
  menuItemName?: string; // optional link to revenue item
  categoryId?: string;
  ingredients: Omit<CostPriceIngredient, 'id' | 'currentPrice' | 'currentPriceDate'>[];
  isCustomItem?: boolean;
  customItemPrice?: number;
  customItemPriceExVat?: number;
  costPriceType?: string;
}

export interface CostPriceUpdateRequest {
  expectedUpdatedAt?: string;
  id: string;
  costPriceName?: string;
  menuItemName?: string | null; // can be set to null to unlink
  replaceExistingLink?: boolean; // Transient manual confirmation; never persisted.
  categoryId?: string;
  ingredients?: Omit<CostPriceIngredient, 'id' | 'currentPrice' | 'currentPriceDate'>[];
  customMenuPrice?: number | null;
  customMenuPriceExVat?: number | null;
  costPriceType?: string | null;
  byoChoiceOverrides?: ByoChoiceOverrides | null; // null clears all overrides
  markedIrrelevant?: boolean;
}

export async function fetchCostPriceAnalysesByRestaurant(
  restaurantId: string
): Promise<CostPriceAnalysis[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/costprice/getAll/${restaurantId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch cost price analyses: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  const analyses: CostPriceAnalysis[] = data.map((item: any) => ({
    ...item,
    createdAt: new Date(item.createdAt),
    updatedAt: new Date(item.updatedAt),
    ingredients: item.ingredients.map((ing: any) => ({
      ...ing,
      currentPriceDate: ing.currentPriceDate ? new Date(ing.currentPriceDate) : undefined
    }))
  }));
  // Keeps the onboarding practice dish in the list across the refetch the editor
  // triggers after every ingredient add. No-op outside a practice branch.
  return withPracticeCostPrices(restaurantId, analyses);
}

export async function fetchCostPriceAnalysesForAllRestaurants(): Promise<CostPriceAnalysis[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/costprice/getAllForAllRestaurants`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch cost price analyses for all restaurants: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return data.map((item: any) => ({
    ...item,
    createdAt: new Date(item.createdAt),
    updatedAt: new Date(item.updatedAt),
    ingredients: item.ingredients.map((ing: any) => ({
      ...ing,
      currentPriceDate: ing.currentPriceDate ? new Date(ing.currentPriceDate) : undefined
    }))
  }));
}

export async function fetchCostPriceAnalysisById(id: string): Promise<CostPriceAnalysis> {
  // Onboarding practice dish: answered from memory, never fetched.
  const practice = practiceCostPriceById(id);
  if (practice) return practice;
  const token = await getAccessToken();
  const res = await fetch(`/api/costprice/${id}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch cost price analysis: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return {
    ...data,
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt),
    ingredients: data.ingredients.map((ing: any) => ({
      ...ing,
      currentPriceDate: ing.currentPriceDate ? new Date(ing.currentPriceDate) : undefined
    }))
  };
}

// Thrown when the server refuses to give a revenue (POS) item a SECOND analysis
// (route responds 409 { error: 'duplicate_menu_item', costPriceName }). Two analyses
// on one revenue item make every needs engine charge that item's sales twice, so the
// link is exclusive — see CostPriceDuplicateMenuItemError on the server.
export class CostPriceDuplicateMenuItemApiError extends Error {
  constructor(
    public existingCostPriceName?: string,
    public existingCostPriceNames: string[] = existingCostPriceName ? [existingCostPriceName] : [],
  ) {
    super('This revenue item is already linked to another cost price analysis');
    this.name = 'CostPriceDuplicateMenuItemApiError';
  }
}

/** The 409 body a duplicate-link rejection carries, or null for any other failure. */
async function readDuplicateMenuItem(res: Response): Promise<CostPriceDuplicateMenuItemApiError | null> {
  if (res.status !== 409) return null;
  let body: any = null;
  try {
    body = await res.json();
  } catch {
    /* no JSON body */
  }
  return body?.error === 'duplicate_menu_item'
    ? new CostPriceDuplicateMenuItemApiError(body?.costPriceName, body?.costPriceNames ?? undefined)
    : null;
}

export async function createCostPriceAnalysis(
  request: CostPriceCreateRequest
): Promise<CostPriceAnalysis> {
  const practice = practiceCreateCostPrice(request);
  if (practice) return practice;
  const token = await getAccessToken();
  const res = await fetch(`/api/costprice/create`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request)
  });
  if (!res.ok) {
    const duplicate = await readDuplicateMenuItem(res);
    if (duplicate) throw duplicate;
    throw new Error(`Failed to create cost price analysis: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return {
    ...data,
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt),
    ingredients: data.ingredients.map((ing: any) => ({
      ...ing,
      currentPriceDate: ing.currentPriceDate ? new Date(ing.currentPriceDate) : undefined
    }))
  };
}

// Thrown when the server rejects a rename because another analysis of the same
// restaurant already uses that name (route responds 409 { error: 'duplicate_name' }).
// Mirrors SubrecipeDuplicateNameApiError; consumed by the cost-price detail modal.
export class CostPriceDuplicateNameApiError extends Error {
  constructor() {
    super('A cost price analysis with this name already exists');
    this.name = 'CostPriceDuplicateNameApiError';
  }
}

export async function updateCostPriceAnalysis(
  request: CostPriceUpdateRequest
): Promise<CostPriceAnalysis> {
  // Onboarding practice dish: applied to the in-memory copy, never persisted.
  const practice = practiceUpdateCostPrice(request);
  if (practice) return practice;
  const token = await getAccessToken();
  const res = await fetch(`/api/costprice/update`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request)
  });
  if (!res.ok) {
    if (res.status === 409) {
      // One read only — the body stream can't be consumed twice.
      let body: any = null;
      try {
        body = await res.json();
      } catch {
        /* no JSON body */
      }
      if (body?.error === 'stale_parent') throw await staleRecipeWriteError();
      if (body?.error === 'duplicate_name') {
        throw new CostPriceDuplicateNameApiError();
      }
      if (body?.error === 'duplicate_menu_item') {
        throw new CostPriceDuplicateMenuItemApiError(body?.costPriceName, body?.costPriceNames ?? undefined);
      }
    }
    throw new Error(`Failed to update cost price analysis: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return {
    ...data,
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt),
    ingredients: data.ingredients.map((ing: any) => ({
      ...ing,
      currentPriceDate: ing.currentPriceDate ? new Date(ing.currentPriceDate) : undefined
    }))
  };
}

export async function deleteCostPriceAnalysis(id: string): Promise<void> {
  // Onboarding practice dish: cleared in memory, never deleted server-side.
  if (practiceDeleteCostPrice(id)) return;
  const token = await getAccessToken();
  const res = await fetch(`/api/costprice/${id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to delete cost price analysis: ${res.status} ${res.statusText}`);
  }
}

export async function fetchRevenueItemNames(restaurantId: string): Promise<string[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/costprice/revenue-items/${restaurantId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch revenue item names: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

export async function fetchRevenueItemNamesBySourceType(restaurantId: string, sourceType: string): Promise<string[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/costprice/revenue-items/${restaurantId}/${sourceType}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch revenue item names by source type: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

export async function deleteBulkCostPriceData(
  restaurantId: string,
  options: { deleteItems: boolean; deleteCategories: boolean; itemIds?: string[]; clearIngredientsOnly?: boolean }
): Promise<{ deletedItems: number; deletedCategories: number; clearedItems: number }> {
  const token = await getAccessToken();
  const res = await fetch(`/api/costprice/deleteBulk/${restaurantId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(options),
  });
  if (!res.ok) {
    throw new Error(`Failed to bulk delete cost price data: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

export async function deleteAllCostPriceData(restaurantId: string): Promise<{ deletedItems: number; deletedCategories: number }> {
  const token = await getAccessToken();
  const res = await fetch(`/api/costprice/deleteAll/${restaurantId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to delete all cost price data: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

// --- Custom ingredient link automation (spec 2) -----------------------------
// Mirrors the server contract in `server/domains/costPrice/customLinkService.ts`.
// A custom ingredient is a placeholder price the user typed before the product ever
// arrived in order history; these two calls plan and then apply re-pointing every
// reference to it at real purchase data. The dry-run WRITES NOTHING.

/** The `{stdAmount, stdMultiplier, stdUnit}` snapshot every ingredient row carries. */
export interface IngredientQuantityInfo {
  stdAmount: number;
  stdMultiplier: number;
  stdUnit: string;
}

export type CustomLinkStatus = 'auto' | 'review' | 'unmatched';

export interface CustomLinkSuggestion {
  platform: string;
  /** `''` for platforms in `platformsWithoutArticleId` — identity then keys off the name. */
  articleId: string;
  productName: string;
  /** Display alias for `platform` (the supplier the product is bought from). */
  supplier?: string;
  quantityInfo: IngredientQuantityInfo;
}

/** One place a custom ingredient is referenced from. `id` is the analysis / subrecipe id. */
export interface CustomLinkUsage {
  kind: 'dish' | 'subrecipe';
  id: string;
  name: string;
  ingredientId: string;
  quantityNeeded: number;
  /**
   * The row's OWN snapshotted unit, not the custom ingredient's current one. `quantityInfo`
   * is frozen at add time, so editing a custom ingredient's unit afterwards leaves older
   * rows on the old unit — each usage converts against what it actually stores.
   */
  stdUnit: string;
}

export interface CustomLinkPlanRow {
  /** canonicalProductKey of the custom ingredient — grouping identity and the commit's handle. */
  customKey: string;
  articleId: string;
  platform: string;
  customPlatformName?: string;
  productName: string;
  /** The stored custom price, per stdUnit. Stored, not derived — safe to show. */
  currentPrice: number;
  stdUnit: string;
  status: CustomLinkStatus;
  best?: {
    platform: string;
    articleId: string;
    productName: string;
    quantityInfo: IngredientQuantityInfo;
    confidence: number;
    method: 'exact' | 'ai';
  };
  suggestions: CustomLinkSuggestion[];
  /**
   * Set when the candidate's stdUnit is a different TYPE from a usage's — always forces review.
   * Computed server-side against `best`, so it says nothing once the user binds a different
   * target. The UI therefore derives its own warning from the BOUND target
   * (`usagesWithDefaultedQuantity`) and does not render this field; kept to mirror the contract.
   */
  unitChange?: { from: string; to: string };
  /** The product is already an ingredient row on ≥1 affected analysis — never auto-bound. */
  alreadyOnDish?: boolean;
  /**
   * ≥1 usage stores the placeholder unit `'unit'` — the inventory Excel import's marker for "the
   * unit could not be parsed". Deliberately NOT `unitChange`: the quantity converts correctly
   * (`unit` is a count unit, 1:1 with `p`), so no amber unit-change line fires and this flag is
   * the only thing marking the row. Means "confirm this one by hand", not "the amount is a guess".
   */
  unknownSourceUnit?: boolean;
  usages: CustomLinkUsage[];
}

export interface CustomLinkPlanSummary {
  total: number;
  auto: number;
  review: number;
  unmatched: number;
  orphanCustomIngredients: number;
  availableProducts: number;
}

export interface CustomLinkPlan {
  rows: CustomLinkPlanRow[];
  summary: CustomLinkPlanSummary;
}

export type CustomLinkResolution =
  | {
      customKey: string;
      action: 'bind';
      platform: string;
      articleId: string;
      productName: string;
      quantityInfo: IngredientQuantityInfo;
      /** Per-usage quantity, keyed by the usage's ingredientId. Always sent by this client. */
      quantityByIngredientId?: Record<string, number>;
      method: 'exact' | 'ai' | 'manual';
    }
  | {
      customKey: string;
      action: 'bindSubrecipe';
      subrecipeId: string;
      /** Portions per usage, keyed by ingredientId. No deterministic fallback exists for portions. */
      portionsByIngredientId: Record<string, number>;
    };

export interface CustomLinkCommitSummary {
  /** Ingredient ROWS re-pointed (a custom ingredient used on 6 dishes contributes 6). */
  linked: number;
  failed: number;
  dishesTouched: number;
  subrecipesTouched: number;
}

/** Plan every custom ingredient in the restaurant's cost model. Writes nothing. */
export async function dryRunCustomLinks(restaurantId: string): Promise<CustomLinkPlan> {
  const token = await getAccessToken();
  const res = await fetch(`/api/costprice/custom-link/dry-run`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ restaurantId }),
  });
  if (!res.ok) {
    throw new Error(`Failed to analyse custom ingredient links: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

/** Apply only the resolutions the user accepted. Never deletes a `custom_ingredients` row. */
export async function commitCustomLinks(
  restaurantId: string,
  resolutions: CustomLinkResolution[]
): Promise<CustomLinkCommitSummary> {
  const token = await getAccessToken();
  const res = await fetch(`/api/costprice/custom-link/commit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ restaurantId, resolutions }),
  });
  if (!res.ok) {
    throw new Error(`Failed to apply custom ingredient links: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return data.summary;
}
